// src/components/DashboardMobileView.tsx
import React from "react";
import {ChevronDown, ChevronUp, Eye, MessageCircle} from "lucide-react";
import {formatDateNumeric} from "../../../utils/dateUtils.ts";
import {DashboardOrder} from "../../../api/ApiTypes.ts";
import {AIBadge} from "../../../components/AIBadge.tsx";
import {StatusBadge} from "../../../components/StatusBadge.tsx";

interface Props {
    data: DashboardOrder[];
    expandedCardIds: number[];
    onToggleExpand: (id: number) => void;
    onView: (id: number) => void;
    getDueDateClass: (dueDate: string | null) => string;
}

export const DashboardMobileView: React.FC<Props> = ({
                                                         data,
                                                         expandedCardIds,
                                                         onToggleExpand,
                                                         onView,
                                                         getDueDateClass,
                                                     }) => {
    const isExpanded = (id: number) => expandedCardIds.includes(id);

    return (
        <div className="md:hidden space-y-4">
            {data.map((order) => (
                <div
                    key={order.id}
                    className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden"
                >
                    {/* Card Header */}
                    <div className="p-4 flex items-center justify-between border-b border-gray-100">
                        <div className="flex items-center space-x-3">
                            <span className="font-medium text-gray-900 text-center relative">
                                {order.usaFileNumber}
                                {/* Message indicator bubble with high z-index */}

                                {order.appraiserUnreadMessages && (
                                    <div
                                        className="absolute z-10 flex items-center justify-center w-4 h-4 bg-red-500 rounded-full text-white text-xs font-bold"
                                        style={{ bottom: "20px", right: "-15px" }}
                                    >
                                        <MessageCircle size={10} className="text-white" />
                                    </div>
                                )}
                            </span>
                            <AIBadge
                                attentionIndicator={order.attentionIndicator}
                            />
                        </div>
                        <div className="flex items-center space-x-2">
                            <button
                                onClick={() => onView(order.id)}
                                className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors"
                            >
                                <Eye className="h-5 w-5" />
                            </button>
                            <button
                                onClick={() => onToggleExpand(order.id)}
                                className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors"
                            >
                                {isExpanded(order.id) ? (
                                    <ChevronUp className="h-5 w-5" />
                                ) : (
                                    <ChevronDown className="h-5 w-5" />
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Card Content */}
                    <div className="p-4 flex flex-col space-y-3">
                        {/* Always visible info */}
                        <StatusBadge
                            statusCode={order.orderStatus}
                            statusId={order.statusId}
                            size={"small"}
                        />
                        <div>
                            <div className="text-sm text-gray-500">Property</div>
                            <div className="font-medium">{order.propertyAddressLine1}</div>
                            <div className="text-sm text-gray-600">
                                {order.propertyCity}, {order.propertyStateCode} {order.propertyZip}
                            </div>
                        </div>

                        <div className="flex justify-between">
                            <div>
                                <div className="text-sm text-gray-500">Borrower</div>
                                <div className="font-medium">{order.borrowerName || "-"}</div>
                            </div>
                            <div>
                                <div className="text-sm text-gray-500">Due Date</div>
                                <span
                                    className={`px-2 py-1 rounded-full text-sm ${getDueDateClass(
                                        order.appraiserDueDate
                                    )}`}
                                >
                                    {formatDateNumeric(order.appraiserDueDate)}
                                </span>
                            </div>
                        </div>

                        {/* Expandable content */}
                        {isExpanded(order.id) && (
                            <div className="pt-2 border-t border-gray-100 mt-2 space-y-3">
                                <div className="flex justify-between">
                                    <div>
                                        <div className="text-sm text-gray-500">Appraiser</div>
                                        <div className="font-medium">
                                            {order.appraiserName || "-"}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-sm text-gray-500">Assigned</div>
                                        <div className="font-medium">
                                            {formatDateNumeric(order.assignmentActualDate)}
                                        </div>
                                    </div>
                                </div>
                                {order.inspectionActualDate && (
                                    <div>
                                        <div className="text-sm text-gray-500">Inspected</div>
                                        <div className="font-medium">
                                            {formatDateNumeric(order.inspectionActualDate)}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};
